import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * KioskAutoScroll — background "office display" behavior.
 *
 * Behavior (updated 2026-02-14):
 *   - Enabled only when the browser is in fullscreen AND the current route is
 *     one of /dev-plan, /production, /operations, /fleet.
 *   - Waits **15 minutes of no user interaction** before starting to scroll.
 *   - Once armed, gently scrolls every `[data-kiosk-scroll]` element from top
 *     → bottom over ~90s, holds ~15s, then eases back to top over ~45s.
 *   - Loops continuously while the operator stays away.
 *   - Any interaction (mouse/keyboard/wheel/touch) cancels the current cycle
 *     immediately and resets the 15-minute inactivity timer.
 */

const ENABLED_ROUTES = ["/dev-plan", "/production", "/operations", "/fleet"];
const INACTIVITY_MS = 15 * 60 * 1000; // 15 min of no interaction before scroll begins
const DOWN_MS = 90000;                // slow scroll down (~90s)
const HOLD_MS = 15000;                // pause at bottom (~15s)
const UP_MS = 45000;                  // scroll back to top (~45s)
const OVERFLOW_MIN = 24;              // ignore panels that overflow by only a few px

function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/** Animate one scrollable element from current scrollTop → targetTop. */
function animateScroll(el, targetTop, durationMs, abortRef) {
    return new Promise((resolve) => {
        const startTop = el.scrollTop;
        const distance = targetTop - startTop;
        if (Math.abs(distance) < 1 || durationMs <= 0) {
            resolve();
            return;
        }
        const t0 = performance.now();
        const step = (now) => {
            if (abortRef.current) {
                resolve();
                return;
            }
            const raw = Math.min(1, (now - t0) / durationMs);
            el.scrollTop = startTop + distance * easeInOut(raw);
            if (raw < 1) requestAnimationFrame(step);
            else resolve();
        };
        requestAnimationFrame(step);
    });
}

/** One complete cycle: down → hold → up. Aborts cleanly at any point. */
async function runCycle(abortRef) {
    if (abortRef.current) return;
    const targets = Array.from(document.querySelectorAll("[data-kiosk-scroll]")).filter(
        (el) => el && el.scrollHeight - el.clientHeight > OVERFLOW_MIN
    );
    if (targets.length === 0) return;

    await Promise.all(
        targets.map((el) => animateScroll(el, el.scrollHeight - el.clientHeight, DOWN_MS, abortRef))
    );
    if (abortRef.current) return;

    await new Promise((r) => setTimeout(r, HOLD_MS));
    if (abortRef.current) return;

    await Promise.all(targets.map((el) => animateScroll(el, 0, UP_MS, abortRef)));
}

function resetAllScroll() {
    document.querySelectorAll("[data-kiosk-scroll]").forEach((el) => {
        el.scrollTop = 0;
    });
}

export default function KioskAutoScroll() {
    const location = useLocation();
    const abortRef = useRef(false);
    const idleTimerRef = useRef(null);
    const runningRef = useRef(false);

    useEffect(() => {
        // Touch-primary devices (phones, tablets) never fullscreen the kiosk
        // and don't benefit from auto-scroll cycles. Bail immediately so no
        // event listeners are attached — keeps mobile scrolling snappy.
        const isTouch = typeof window !== "undefined" &&
            (window.matchMedia?.("(pointer: coarse)").matches ||
             ("ontouchstart" in window && !window.matchMedia?.("(pointer: fine)").matches));
        if (isTouch) return;

        const routeEnabled = ENABLED_ROUTES.some(
            (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
        );

        function armInactivityTimer() {
            clearInactivityTimer();
            if (!routeEnabled || !document.fullscreenElement) return;
            idleTimerRef.current = setTimeout(startLoop, INACTIVITY_MS);
        }

        function clearInactivityTimer() {
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        }

        async function startLoop() {
            if (runningRef.current) return;
            runningRef.current = true;
            abortRef.current = false;
            try {
                // Loop until aborted by an interaction, route change, or fullscreen exit.
                while (!abortRef.current && document.fullscreenElement && routeEnabled) {
                    await runCycle(abortRef);
                    if (abortRef.current) break;
                }
            } finally {
                runningRef.current = false;
            }
        }

        function stopEverything() {
            const wasRunning = runningRef.current;
            abortRef.current = true;
            runningRef.current = false;
            clearInactivityTimer();
            // Only reset scroll positions if a kiosk cycle was actually running
            // — otherwise we'd destroy the user's manual scroll state.
            if (wasRunning) resetAllScroll();
        }

        function onInteraction() {
            // Any real interaction: kill the current cycle AND restart the
            // 15-minute inactivity countdown from zero. Do NOT touch scroll
            // positions on normal interaction — mousemove/wheel fire dozens
            // of times per second and would fight the user's own scrolling.
            if (runningRef.current) {
                abortRef.current = true;
                // Cycle was mid-flight — leave scrollTop wherever the abort
                // lands it; the user is now in control.
            }
            armInactivityTimer();
        }

        function onFsChange() {
            if (document.fullscreenElement) {
                armInactivityTimer();
            } else {
                stopEverything();
            }
        }

        // Arm on mount if we're already fullscreen + on an eligible route.
        if (document.fullscreenElement && routeEnabled) {
            armInactivityTimer();
        }

        document.addEventListener("fullscreenchange", onFsChange);
        window.addEventListener("mousedown", onInteraction);
        window.addEventListener("mousemove", onInteraction, { passive: true });
        window.addEventListener("keydown", onInteraction);
        window.addEventListener("wheel", onInteraction, { passive: true });
        window.addEventListener("touchstart", onInteraction, { passive: true });

        return () => {
            stopEverything();
            document.removeEventListener("fullscreenchange", onFsChange);
            window.removeEventListener("mousedown", onInteraction);
            window.removeEventListener("mousemove", onInteraction);
            window.removeEventListener("keydown", onInteraction);
            window.removeEventListener("wheel", onInteraction);
            window.removeEventListener("touchstart", onInteraction);
        };
    }, [location.pathname]);

    return null;
}
